// LE PORTAIL — le sas franchi avant que le jeu ne se charge.
//
// Il répond à une seule question : « ce client a-t-il le droit de jouer
// maintenant ? ». Quatre raisons de dire non :
//
//   1. MAINTENANCE   — le serveur est fermé, personne n'entre ;
//   2. MISE À JOUR    — le build installé est plus vieux que `buildMinimum` ;
//   3. SERVEUR ANCIEN — le déploiement n'a pas encore rejoint le client ;
//   4. HORS LIGNE     — le serveur n'a pas été joint depuis plus de 24 h.
//
// Quand la réponse est oui — le cas courant — le Portail n'affiche RIEN et ne
// demande RIEN : le joueur ne sait pas qu'il existe. C'est le but. Un sas qui
// se voit à chaque lancement, c'est un clic de plus par session pour rien.
//
// Le blocage se décide sur un NOMBRE (le build, entier strictement croissant),
// jamais sur l'étiquette « majeure » : « 0.1.10 » vs « 0.1.9 » comparés comme
// du texte donneraient le mauvais résultat, et une étiquette ne se compare pas.
// L'étiquette ne sert qu'à formuler la phrase affichée.
//
// La règle des 24 h existe parce que cette version est un prototype : sans
// elle, une build de test pourrait continuer à tourner des semaines sur un
// téléphone hors réseau, sans jamais apprendre qu'elle est périmée.

import { isNativeApp } from './serverConfig.js';
import { lireDesktopInstalle, telechargerInstallateur } from './desktopUpdate.js';
import { clearObsoleteCodeCaches } from '../shared/mediaCache.js';

const CLE_DERNIERE_VERIF = 'guild.portail.derniereVerif';
const CLE_HORLOGE_MAX = 'guild.portail.horlogeMax';
const CLE_GRACE = 'guild.portail.graceHeures';

const GRACE_DEFAUT_H = 24;
const DELAI_REQUETE_MS = 8000;
const RE_ESSAI_MS = 20000;

export interface InfosPortail {
  version: string;
  build: number;
  disponible: boolean;
  taille: number;
  url: string;
  desktop: {
    disponible: boolean;
    taille: number;
    url: string;
  };
  buildMinimum: number;
  criticite: 'majeure' | 'mineure';
  message: string;
  maintenance: boolean;
  messageMaintenance: string;
  graceHorsLigneHeures: number;
}

/** Verdict du Portail. `ok` : on entre. Sinon on reste, écran à l'appui. */
type Verdict =
  | { etat: 'ok' }
  | { etat: 'maintenance'; infos: InfosPortail }
  | { etat: 'maj'; infos: InfosPortail }
  | { etat: 'serveurAncien'; infos: InfosPortail; buildClient: number }
  | { etat: 'horsLigne' };

function lire(cle: string): number {
  try { return Number(localStorage.getItem(cle)) || 0; } catch { return 0; }
}
function ecrire(cle: string, valeur: number) {
  try { localStorage.setItem(cle, String(valeur)); } catch { /* mode privé */ }
}

/** Révoque toute autorisation hors ligne encore mémorisée. */
export function revoquerAutorisationPortail() {
  ecrire(CLE_DERNIERE_VERIF, 0);
}

/**
 * Horodatage de confiance.
 *
 * Reculer l'horloge du téléphone rendrait la fenêtre de grâce éternelle : on
 * garde donc la date la plus haute jamais vue. Si l'horloge est revenue en
 * arrière, on ne cherche pas à deviner de combien — on considère simplement la
 * grâce comme épuisée, ce qui remet le joueur face au serveur. Ce n'est pas de
 * la protection anti-triche : c'est refuser de faire confiance à une horloge
 * qu'on sait fausse.
 */
function horlogeCoherente(): boolean {
  const maintenant = Date.now();
  const max = lire(CLE_HORLOGE_MAX);
  if (maintenant + 60_000 < max) return false;   // 1 min de tolérance (dérive NTP)
  if (maintenant > max) ecrire(CLE_HORLOGE_MAX, maintenant);
  return true;
}

function lireGraceHeures(): number {
  try {
    const brute = localStorage.getItem(CLE_GRACE);
    if (brute === null) return GRACE_DEFAUT_H;
    const heures = Number(brute);
    return Number.isFinite(heures) && heures >= 0 ? heures : GRACE_DEFAUT_H;
  } catch {
    return GRACE_DEFAUT_H;
  }
}

function graceMs(): number {
  return lireGraceHeures() * 3600_000;
}

/** Le serveur a-t-il autorisé ce client assez récemment pour jouer sans lui ? */
function dansLaFenetreDeGrace(): boolean {
  if (!horlogeCoherente()) return false;
  const derniere = lire(CLE_DERNIERE_VERIF);
  if (!derniere) return false;                    // jamais joint : première fois
  if (derniere > Date.now() + 60_000) return false; // date future : incohérent
  return Date.now() - derniere < graceMs();
}

/** Interroge le serveur. `null` = injoignable (et non « rien à signaler »). */
async function interroger(): Promise<InfosPortail | null> {
  const base = globalThis.__GUILD_SERVER_URL__ || '';
  const ctrl = new AbortController();
  const minuteur = setTimeout(() => ctrl.abort(), DELAI_REQUETE_MS);
  try {
    const rep = await fetch(`${base}/app-version`, {
      signal: ctrl.signal,
      cache: 'no-store',
      mode: 'cors',
    });
    if (!rep.ok) return null;
    const data = await rep.json();
    if (!data || typeof data !== 'object') return null;
    return {
      version: String(data.version ?? '0.0.0'),
      build: Number(data.build) || 0,
      disponible: Boolean(data.disponible),
      taille: Number(data.taille) || 0,
      url: String(data.url ?? ''),
      desktop: {
        disponible: Boolean(data.desktop?.disponible),
        taille: Number(data.desktop?.taille) || 0,
        url: String(data.desktop?.url ?? ''),
      },
      buildMinimum: Number(data.buildMinimum) || 0,
      criticite: data.criticite === 'majeure' ? 'majeure' : 'mineure',
      message: String(data.message ?? ''),
      maintenance: Boolean(data.maintenance),
      messageMaintenance: String(data.messageMaintenance ?? ''),
      graceHorsLigneHeures: Number.isFinite(Number(data.graceHorsLigneHeures))
        && Number(data.graceHorsLigneHeures) >= 0
        ? Number(data.graceHorsLigneHeures)
        : GRACE_DEFAUT_H,
    };
  } catch {
    return null;                                  // hors ligne, DNS mort, timeout
  } finally {
    clearTimeout(minuteur);
  }
}

/** Un aller-retour serveur → verdict. Ne touche à aucun affichage. */
async function statuer(): Promise<Verdict> {
  const infos = await interroger();

  if (!infos) {
    // En développement, un serveur de jeu simplement pas encore démarré
    // bloquerait l'écran au premier lancement (aucune vérification en mémoire) :
    // on ne peut plus travailler tant qu'on n'a pas lancé `npm run server`.
    // On laisse donc passer, bruyamment. `guild.portail.strict = 1` dans le
    // stockage local rétablit le comportement de production pour le tester.
    let strict = false;
    try { strict = localStorage.getItem('guild.portail.strict') === '1'; } catch { /* mode privé */ }
    if (import.meta.env.DEV && !strict) {
      console.warn('[PORTAIL] serveur injoignable — laissé passer (développement).');
      return { etat: 'ok' };
    }
    return dansLaFenetreDeGrace() ? { etat: 'ok' } : { etat: 'horsLigne' };
  }

  if (infos.maintenance) {
    revoquerAutorisationPortail();
    return { etat: 'maintenance', infos };
  }
  const desktop = await lireDesktopInstalle();
  // Electron charge toujours le Web le plus récent : __APP_BUILD__ décrit
  // donc la page, pas l'application Windows installée. Le preload fournit le
  // vrai build ; les anciens Electron (build 0) passent par une MAJ relais.
  // Le runtime de développement n'est pas un conteneur distribué : il annonce
  // le build du code local, exactement comme le handshake Socket.io. Le laisser
  // passer sans comparaison créerait une boucle Portail OK → socket refusée.
  const buildClient = desktop && !desktop.development
    ? desktop.build
    : Number(__APP_BUILD__);
  if (buildClient > infos.build) {
    revoquerAutorisationPortail();
    return { etat: 'serveurAncien', infos, buildClient };
  }
  if (buildClient < infos.buildMinimum) {
    revoquerAutorisationPortail();
    return { etat: 'maj', infos };
  }

  // La grâce hors ligne atteste une AUTORISATION récente, pas seulement un
  // serveur récemment joignable. L'écrire avant les verdicts ci-dessus
  // permettrait à un ancien client refusé (ou à un client en maintenance) de
  // couper le réseau puis d'entrer au prochain essai pendant toute la grâce.
  horlogeCoherente();
  ecrire(CLE_DERNIERE_VERIF, Date.now());
  ecrire(CLE_GRACE, infos.graceHorsLigneHeures);
  return { etat: 'ok' };
}

// ── Écran ───────────────────────────────────────────────────────────────────
// En DOM pur, sans Vue ni composant du jeu : le Portail doit pouvoir afficher
// « mets-toi à jour » PRÉCISÉMENT dans le cas où le reste du bundle est vieux,
// cassé, ou pas encore chargé. Une dépendance de plus ici, c'est un écran de
// blocage qui peut lui-même planter.

const CSS = `
.guild-portail, .guild-portail * { box-sizing: border-box; }
.guild-portail {
  position: fixed; inset: 0; z-index: 2147483000;
  display: flex; align-items: center; justify-content: center;
  padding: calc(24px + env(safe-area-inset-top)) 24px calc(24px + env(safe-area-inset-bottom));
  background: radial-gradient(120% 90% at 50% 0%, #17123a 0%, #0d0a1a 60%, #07050f 100%);
  color: #e8f6ff; font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  animation: guildPortailEntre .25s ease-out both;
}
@keyframes guildPortailEntre { from { opacity: 0; } }
.guild-portail__boite {
  width: min(26rem, 100%); text-align: center;
}
.guild-portail__sceau {
  width: 68px; height: 68px; margin: 0 auto 20px;
  border-radius: 50%; border: 1px solid rgba(111, 242, 230, .45);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.8rem;
  background: rgba(111, 242, 230, .07);
  box-shadow: 0 0 28px rgba(111, 242, 230, .18), inset 0 0 18px rgba(111, 242, 230, .08);
}
.guild-portail__titre {
  font-size: 1.32rem; font-weight: 600; color: #6ff2e6;
  letter-spacing: .02em; margin-bottom: 10px;
}
.guild-portail__texte {
  font-size: .93rem; line-height: 1.6; color: #a8c4d4; margin-bottom: 22px;
  white-space: pre-line;
}
.guild-portail__actions { display: flex; flex-direction: column; gap: 10px; }
.guild-portail button {
  width: 100%; padding: .82rem 1rem; border-radius: 10px;
  font-size: .95rem; font-weight: 600; font-family: inherit; cursor: pointer;
  border: 1px solid rgba(111, 242, 230, .55);
  background: rgba(79, 232, 224, .16); color: #6ff2e6;
  transition: background .15s ease;
}
.guild-portail button:hover { background: rgba(79, 232, 224, .3); }
.guild-portail button:disabled { opacity: .5; cursor: default; }
.guild-portail button.secondaire {
  background: none; border-color: rgba(168, 196, 212, .28); color: #a8c4d4;
}
.guild-portail__pied {
  margin-top: 20px; font-size: .76rem; color: #5d7183; line-height: 1.5;
}
`;

let racineEcran: HTMLElement | null = null;
let derniereSignature = '';

/** Relance du cycle, réassignée à chaque tour de boucle (voir plus bas). */
let relanceEnCours: (() => void) | null = null;
function reessayerMaintenant() { relanceEnCours?.(); }

function poids(octets: number) {
  return octets > 0 ? ` (${(octets / 1024 / 1024).toFixed(0)} Mo)` : '';
}

interface Bouton { libelle: string; action: () => void; secondaire?: boolean }

function afficher(opts: { sceau: string; titre: string; texte: string; boutons: Bouton[]; pied?: string }) {
  // Le Portail se réinterroge tout seul toutes les 20 s. Sans ce garde-fou,
  // chaque tentative reconstruirait l'écran à l'identique — donc un clignotement
  // régulier, et un bouton qui se dérobe sous le doigt au mauvais moment.
  const signature = JSON.stringify([opts.sceau, opts.titre, opts.texte, opts.boutons.map((b) => b.libelle)]);
  if (racineEcran && signature === derniereSignature) return;
  derniereSignature = signature;

  if (!document.getElementById('guild-portail-style')) {
    const style = document.createElement('style');
    style.id = 'guild-portail-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  racineEcran?.remove();
  const boite = document.createElement('div');
  boite.className = 'guild-portail';
  boite.setAttribute('role', 'alertdialog');
  boite.innerHTML = `
    <div class="guild-portail__boite">
      <div class="guild-portail__sceau">${opts.sceau}</div>
      <div class="guild-portail__titre"></div>
      <div class="guild-portail__texte"></div>
      <div class="guild-portail__actions"></div>
      <div class="guild-portail__pied"></div>
    </div>
  `;
  // textContent et non innerHTML : le message vient du serveur, il n'a aucune
  // raison de pouvoir injecter du balisage dans la page.
  boite.querySelector('.guild-portail__titre')!.textContent = opts.titre;
  boite.querySelector('.guild-portail__texte')!.textContent = opts.texte;
  boite.querySelector('.guild-portail__pied')!.textContent =
    opts.pied ?? `La Guilde des Aventuriers — v${__APP_VERSION__} (build ${__APP_BUILD__})`;

  const actions = boite.querySelector('.guild-portail__actions')!;
  for (const b of opts.boutons) {
    const bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.textContent = b.libelle;
    if (b.secondaire) bouton.className = 'secondaire';
    bouton.addEventListener('click', b.action);
    actions.appendChild(bouton);
  }

  document.body.appendChild(boite);
  racineEcran = boite;
}

function effacer() {
  racineEcran?.remove();
  racineEcran = null;
  derniereSignature = '';
  document.getElementById('guild-portail-style')?.remove();
}

/**
 * État de sûreté si le Portail lui-même rencontre une erreur inattendue.
 *
 * Le jeu ne doit pas devenir accessible parce que son contrôle d'entrée a
 * planté. Cet écran reste volontairement dans le même objet-interface que les
 * autres verdicts du Portail et ne dépend d'aucun composant Vue.
 */
export function afficherErreurPortail() {
  afficher({
    sceau: '!',
    titre: 'Le Portail ne répond pas',
    texte: 'Le registre d’entrée de la Guilde n’a pas pu être consulté. Réessaie dans un instant.',
    boutons: [{ libelle: 'Réessayer', action: () => window.location.reload() }],
  });
}

/** Échec public du chargement du chunk du jeu, après autorisation. */
export function afficherErreurDemarrage() {
  afficher({
    sceau: '!',
    titre: 'Chargement interrompu',
    texte: 'Les portes se sont ouvertes, mais le jeu n’a pas pu finir de se charger. Recharge les fichiers depuis le réseau.',
    boutons: [{
      libelle: 'Recharger le jeu',
      action: () => { void rechargerDepuisLeReseau(); },
    }],
  });
}

/**
 * Recharge le site en repartant du réseau.
 *
 * Un simple `location.reload()` ne suffit pas côté web : le service worker
 * resservirait l'ancien index.html depuis son cache, et l'écran de mise à jour
 * obligatoire réapparaîtrait en boucle — le joueur cliquerait « Mettre à jour »
 * indéfiniment sur la même version. On retire donc le service worker et ses
 * caches de CODE avant de recharger ; les médias identifiés par OID restent
 * valables et ne doivent pas être téléchargés à nouveau à chaque mise à jour.
 */
export async function rechargerDepuisLeReseau() {
  try {
    if ('serviceWorker' in navigator) {
      const inscriptions = await navigator.serviceWorker.getRegistrations();
      await Promise.all(inscriptions.map((i) => i.unregister()));
    }
    await clearObsoleteCodeCaches();
  } catch { /* rien à nettoyer, ou stockage refusé : on recharge quand même */ }
  location.reload();
}

// ── Boucle ──────────────────────────────────────────────────────────────────

/**
 * Franchit le Portail.
 *
 * Résout quand le joueur a le droit d'entrer — et NE RÉSOUT JAMAIS tant qu'il
 * ne l'a pas. C'est volontaire : le jeu n'est même pas chargé derrière, il n'y
 * a donc rien à contourner en fermant une fenêtre. Tant qu'un écran est
 * affiché, on retente tout seul toutes les 20 s : une maintenance qui se
 * termine, ou le réseau qui revient, débloque sans que le joueur touche à rien.
 */
export async function franchirLePortail(): Promise<void> {
  for (;;) {
    const verdict = await statuer();

    if (verdict.etat === 'ok') {
      effacer();
      return;
    }

    let relancer!: () => void;
    const attente = new Promise<void>((resolve) => { relancer = resolve; });
    const auto = setTimeout(relancer, RE_ESSAI_MS);
    // Les boutons passent par `reessayerMaintenant`, qui appelle CETTE relance :
    // l'écran survit d'un tour à l'autre, ses boutons doivent suivre.
    relanceEnCours = () => { clearTimeout(auto); relancer(); };
    const reessayer = reessayerMaintenant;

    if (verdict.etat === 'maintenance') {
      afficher({
        sceau: '⚙',
        titre: 'La Guilde est fermée',
        texte: verdict.infos.messageMaintenance
          || 'Le monde est en maintenance. Reviens dans quelques minutes — la\nconnexion se rétablira toute seule dès la réouverture.',
        boutons: [{ libelle: 'Réessayer', action: reessayer, secondaire: true }],
      });
    } else if (verdict.etat === 'maj') {
      const { infos } = verdict;
      const desktop = await lireDesktopInstalle();
      const desktopDistribue = desktop && !desktop.development ? desktop : null;
      const natif = isNativeApp();
      const texte = infos.message
        || (infos.criticite === 'majeure'
          ? `Cette mise à jour est obligatoire pour continuer à jouer.\n${desktopDistribue ? `Ton application Windows est en ${desktopDistribue.legacy ? 'ancienne version' : `v${desktopDistribue.version}`}` : `Tu es en v${__APP_VERSION__}`}, la version du monde est la v${infos.version}.`
          : `Une nouvelle version est requise pour rejoindre le monde.\n${desktopDistribue ? `Ton application Windows est en ${desktopDistribue.legacy ? 'ancienne version' : `v${desktopDistribue.version}`}` : `Tu es en v${__APP_VERSION__}`}, la version du monde est la v${infos.version}.`);

      const boutons: Bouton[] = [];
      if (desktop?.development) {
        // Le runtime lancé depuis node_modules doit rester dans le dépôt :
        // installer le setup public remplacerait le contexte que l'on teste.
        boutons.push({ libelle: 'Réessayer', action: reessayer, secondaire: true });
      } else if (desktopDistribue && infos.desktop.disponible) {
        boutons.push({
          libelle: `Télécharger la v${infos.version}${poids(infos.desktop.taille)}`,
          action: () => {
            const url = `${globalThis.__GUILD_SERVER_URL__ || ''}${infos.desktop.url}`;
            void telechargerInstallateur(url).catch((error) => {
              console.error('[PORTAIL] téléchargement Windows impossible :', error);
              window.alert('Le téléchargement Windows n’a pas pu démarrer. Vérifie la connexion et réessaie.');
            });
          },
        });
        boutons.push({ libelle: 'J’ai installé — relancer', action: reessayer, secondaire: true });
      } else if (desktopDistribue) {
        boutons.push({ libelle: 'Réessayer', action: reessayer, secondaire: true });
      } else if (natif && infos.disponible) {
        boutons.push({
          libelle: `Télécharger la v${infos.version}${poids(infos.taille)}`,
          // DownloadManager Android contourne à la fois la WebView et le
          // service worker de la PWA, qui pouvait renvoyer index.html ici.
          action: () => {
            const url = `${globalThis.__GUILD_SERVER_URL__ || ''}${infos.url}`;
            void import('./apkDownload.js')
              .then((m) => m.telechargerApk(url, infos.version))
              .catch((error) => {
                console.error('[PORTAIL] téléchargement Android impossible :', error);
                window.alert('Le téléchargement Android n’a pas pu démarrer. Vérifie la connexion et réessaie.');
              });
          },
        });
        boutons.push({ libelle: 'J\'ai installé — relancer', action: reessayer, secondaire: true });
      } else if (natif) {
        boutons.push({ libelle: 'Réessayer', action: reessayer, secondaire: true });
      } else {
        boutons.push({ libelle: 'Mettre à jour maintenant', action: () => { void rechargerDepuisLeReseau(); } });
      }

      afficher({
        sceau: '✦',
        titre: 'Mise à jour requise',
        texte: (desktopDistribue && !infos.desktop.disponible) || (natif && !infos.disponible)
          ? `${texte}\n\nAucun fichier d’installation n’est disponible sur le serveur pour le moment.`
          : desktop?.development
            ? `${texte}\n\nMets à jour le code local avant de relancer Electron ; le setup public n’est jamais installé dans le runtime de développement.`
          : texte,
        boutons,
      });
    } else if (verdict.etat === 'serveurAncien') {
      afficher({
        sceau: '…',
        titre: 'Le monde se synchronise',
        texte: `Ton application est plus récente que le serveur actuellement joignable.\n\nLa Guilde termine probablement sa mise à niveau. Cet écran disparaîtra automatiquement dès que le serveur aura rejoint le build ${verdict.buildClient}.`,
        boutons: [{ libelle: 'Réessayer', action: reessayer, secondaire: true }],
      });
    } else {
      const heures = Math.round(graceMs() / 3600_000);
      afficher({
        sceau: '⚡',
        titre: 'Connexion requise',
        texte: `Impossible de joindre la Guilde.\n\nCette version de test doit se signaler au serveur au moins une fois\ntoutes les ${heures} h. Reconnecte-toi à Internet pour continuer.`,
        boutons: [{ libelle: 'Réessayer', action: reessayer }],
      });
    }

    await attente;
  }
}
